import classNames from 'classnames';

type HashClass = string;
type Classes = {
    [bemClass: string]: HashClass,
};

/**
 * Builds a `Proxy`-backed class map that returns both the original BEM class
 * name and its CSS-Module-hashed counterpart joined by a space.
 *
 * When a component uses CSS Modules, the bundler replaces human-readable BEM
 * class names (e.g. `"button"`) with globally unique hashes (e.g.
 * `"button_abc123"`). `hashedClasses` produces an object where looking up a
 * BEM key returns `"{bemClass} {hashedClass}"` — preserving the original class
 * for selector targeting (e.g. in tests) while still applying the scoped hash.
 * If a key is not found in the module map, the Proxy falls through to the
 * original name, preventing runtime errors for unknown classes.
 *
 * @param moduleClasses - The imported CSS Modules object (`import styles from './component.module.styl'`).
 * @returns A Proxy where each BEM key maps to `"{key} {hashedValue}"`,
 *   and unknown keys fall back to the key itself.
 *
 * @example
 * ```ts
 * const classes = hashedClasses(styles);
 * classes.button; // → "button button_abc123"
 * ```
 */
export const hashedClasses = (moduleClasses: Classes): Classes => {
    const target = Object.keys(moduleClasses).reduce((acc, key) => {
        acc[key] = `${key} ${moduleClasses[key]}`;
        return acc;
    }, {} as Record<string, string>);
    const fallback = {
        get(targetObj: Classes, prop: string) {
            return targetObj[prop] ?? prop;
        },
    } as const;
    return new Proxy(target, fallback);
};

/**
 * Creates a `classnames`-compatible function that resolves BEM class names to
 * their CSS-Module-hashed equivalents.
 *
 * This is the primary utility used by UI kit components to apply scoped CSS
 * module styles. Internally it calls `hashedClasses` and then wraps the result
 * in a function that accepts the same arguments as the `classnames` library —
 * strings, arrays, or condition objects — and maps each resolved BEM token to
 * its `"{bemClass} {hashedClass}"` pair.
 *
 * @param moduleClasses - The imported CSS Modules object.
 * @returns A function with the same signature as `classnames(...)` that returns
 *   a space-separated string of both BEM and hashed class names.
 *
 * @example
 * ```ts
 * import styles from './button.module.styl';
 * import {withCssModule} from '@pb/kit-util-with-css-module';
 *
 * const cn = withCssModule(styles);
 *
 * cn('button', {'button--primary': isPrimary});
 * // → "button button_abc123 button--primary button--primary_xyz789"
 * ```
 */
export const withCssModule = (moduleClasses: Classes) => {
    const hashed = hashedClasses(moduleClasses);

    return (...bemClasses: unknown[]): string =>
        classNames(bemClasses)
            .split(' ')
            .map((className: string) => hashed[className])
            .join(' ');
};
